import React, { useState, useEffect } from 'react';
import { Clock, Calendar, ArrowRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, capitalize } from '../../../utilities/functions/Helpers';
import PagesEndpoints from '../../../utilities/constants/PagesEndpoints';

const NextEventHero = ({ nextEvent }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!nextEvent) return;

    const calculateTimeLeft = () => {
      const difference = new Date(nextEvent.dateTime) - new Date();
      
      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        };
      }
      return null; // Event has passed or is happening now
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [nextEvent]);

  // Empty State
  if (!nextEvent) {
    return (
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden shadow-xl">
        <div className="relative z-10 max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-serif">Ready for your next experience?</h2>
          <p className="text-slate-300 mb-8 text-lg">You don't have any upcoming bookings. Start planning your next event or schedule a visit today.</p>
          <button 
            onClick={() => navigate(PagesEndpoints.BOOKING_PAGE_URL)}
            className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-full font-semibold transition-all shadow-lg shadow-blue-600/30 flex items-center gap-2"
          >
            <Plus size={20} />
            Book Now
          </button>
        </div>
        {/* Background Decorative Pattern */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none" />
      </div>
    );
  }

  // Active State
  const isReservation = nextEvent.type === 'reservation';
  const gradientClass = isReservation 
    ? "from-blue-600 to-indigo-700" 
    : "from-purple-600 to-fuchsia-700";

  return (
    <div className={`bg-gradient-to-r ${gradientClass} rounded-2xl p-6 md:p-10 text-white relative overflow-hidden shadow-xl`}>
      <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
        
        {/* Left Side: Event Info */}
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium">
             <Calendar size={16} />
             <span>Up Next: {capitalize(nextEvent.type)}</span>
          </div>
          
          <div>
            <h2 className="text-3xl md:text-5xl font-bold font-serif mb-2 tracking-tight">
              {capitalize(nextEvent.title || 'Upcoming Event')}
            </h2>
            <p className="text-white/80 text-lg flex items-center gap-2">
               <Clock size={20} />
               {formatDateTime(nextEvent.dateTime)}
            </p>
          </div>

          <button 
            onClick={() => navigate(nextEvent.link)}
            className="inline-flex items-center gap-2 text-white font-semibold hover:text-white/80 transition-colors mt-2"
          >
            View Details <ArrowRight size={20} />
          </button>
        </div>

        {/* Right Side: Countdown */}
        {timeLeft && (
          <div className="flex gap-4 md:gap-6 text-center bg-black/20 backdrop-blur-md p-6 rounded-2xl border border-white/10">
            <div>
              <div className="text-3xl md:text-5xl font-bold font-mono">{timeLeft.days}</div>
              <div className="text-xs uppercase tracking-wider opacity-70">Days</div>
            </div>
            <div className="text-3xl md:text-5xl font-bold opacity-50">:</div>
            <div>
              <div className="text-3xl md:text-5xl font-bold font-mono">
                {String(timeLeft.hours).padStart(2, '0')}
              </div>
              <div className="text-xs uppercase tracking-wider opacity-70">Hours</div>
            </div>
             <div className="text-3xl md:text-5xl font-bold opacity-50">:</div>
            <div>
              <div className="text-3xl md:text-5xl font-bold font-mono">
                {String(timeLeft.minutes).padStart(2, '0')}
              </div>
              <div className="text-xs uppercase tracking-wider opacity-70">Mins</div>
            </div>
          </div>
        )}
      </div>

       {/* Background Decoration */}
       <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-96 h-96 bg-white/10 rounded-full blur-3xl pointer-events-none mix-blend-overlay" />
    </div>
  );
};

export default NextEventHero;
